import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Fab, Tooltip } from '@mui/material';
import React from 'react';
import { useLocation } from 'react-router-dom';

const WHATSAPP_LINK = 'https://wa.me/5511925758584';
const MIN_SCROLL_TO_CONSIDER = 180;
const BOTTOM_VISIBILITY_DISTANCE = 360;
// Em páginas com infinite scroll o fim "foge" do usuário (cada página nova aumenta
// a altura), então amarrar ao fim fazia o botão sumir. Aqui ele aparece depois de
// rolar ~uma tela (≈ página 2) e FICA visível.
const SCROLL_PERSISTENTE_INFINITE = 600;

/** Rotas estáticas de 1 segmento que NÃO são lista pública (o catch-all /:slugLista pega o resto) */
const ROTAS_ESTATICAS = new Set([
  'criar-lista',
  'privacy',
  'terms',
  'contact',
  'sobre',
  'presentes',
  'demo',
  'admin',
  'lista-casamento-pix',
  'lista-cha-de-bebe',
]);

const usaInfiniteScroll = (pathname: string): boolean => {
  if (pathname.startsWith('/presentes')) return true;
  if (pathname.startsWith('/lista/')) return true;
  const segmentos = pathname.split('/').filter(Boolean);
  // Catch-all /:slugLista (lista pública com slug direto)
  return segmentos.length === 1 && !ROTAS_ESTATICAS.has(segmentos[0]);
};

const WhatsAppFloatingButton = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => {
    const infinite = usaInfiniteScroll(pathname);

    const updateVisibility = () => {
      const scrollTop = window.scrollY || window.pageYOffset;
      const viewportBottom = scrollTop + window.innerHeight;
      const distanceToBottom = document.documentElement.scrollHeight - viewportBottom;

      // Infinite scroll: aparece após rolar ~uma tela e PERMANECE (não depende do fim).
      // Demais páginas: aparece quando chega perto do fim.
      const shouldShow = infinite
        ? scrollTop > SCROLL_PERSISTENTE_INFINITE
        : scrollTop > MIN_SCROLL_TO_CONSIDER && distanceToBottom <= BOTTOM_VISIBILITY_DISTANCE;

      setIsVisible(shouldShow);
    };

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);

    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [pathname]);

  // Nas telas de Ideias de Presentes (/presentes) o FAB de filtros ocupa esse canto;
  // esconde o WhatsApp por lá para não sobrepor o botão de filtros.
  if (pathname.startsWith('/presentes')) return null;

  return (
    <Tooltip title="Falar no WhatsApp" placement="left">
      <Fab
        size="medium"
        component="a"
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        color="success"
        aria-label="Falar no WhatsApp"
        sx={{
          position: 'fixed',
          right: { xs: 16, sm: 22 },
          bottom: { xs: 18, sm: 24 },
          zIndex: 1300,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
          pointerEvents: isVisible ? 'auto' : 'none',
          transition: 'opacity 180ms ease, transform 180ms ease',
          boxShadow: '0 14px 28px rgba(17, 24, 39, 0.24)',
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 24 }} />
      </Fab>
    </Tooltip>
  );
};

export default WhatsAppFloatingButton;
